import { useStore } from "stimulus-store";
import _ from "lodash";
import { Controller } from "@hotwired/stimulus";

import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications";
import {
    LOCALE_TYPE_AREA,
    LOCALE_TYPE_BUILDING,
    LOCALE_TYPE_REGION,
    LOCALE_TYPE_ROOM
} from "constants/facilities_constants";
import { areaStore } from "../../stores/locales/area_store";
import { roomStore } from "../../stores/locales/room_store";
import { regionStore } from "../../stores/locales/region_store";
import { buildingStore } from "../../stores/locales/building_store";

// Connects to data-controller="forms--locale-form-v2"
export default class extends Controller {
    AREA_FIELDS_WRAPPER = 'area-fields-wrapper';

    static stores = [
        areaStore,
        buildingStore,
        regionStore,
        roomStore
    ];

    static targets = [
        "floorInput",
        "floorPlanName",
        "floorPlanPreviewPlaceholder",
        "floorPlanPreview",
        "form",
        "idInput",
        "managerInput",
        "modal",
        "nameInput",
        "placeholder",
        "submit",
        "title"
    ]

    static values = {
        method: String,
        url: String,
        localeType: String,
        title: String
    }

    connect() {
        useStore(this);

        // Prevent default form submission - handle via AJAX only
        if (this.hasFormTarget) {
            // Remove any action attribute to prevent form submission
            this.formTarget.removeAttribute('action');

            // Prevent default form submission
            this.formTarget.addEventListener('submit', (e) => {
                e.preventDefault();
                e.stopPropagation();
            });
        }

        // Listen for the window event fired by handleAddButtonClick on the device form's controller.
        // This event allows the modal controller to prepare itself using its own store connections.
        // Using a window event is necessary because the device form's controller and modal's
        // controller are separate instances of the same controller class, and store values
        // may not be reliably accessible across instances (especially after form submission).
        // The modal controller uses its own useStore() to ensure it has the latest values.
        window.addEventListener('walkthroughAddLocaleButtonClicked', () => {
            this._prepareModalForDisplay();
        });

        // Also listen for Bootstrap modal events as a fallback for other contexts
        // (e.g., when the modal is opened from facilities tables).
        // We use 'shown.bs.modal' which fires AFTER the modal is fully shown,
        // rather than 'show.bs.modal' which fires before, to ensure the DOM is fully ready.
        if (this.hasModalTarget) {
            this.modalTarget.addEventListener('shown.bs.modal', () => {
                this._prepareModalForDisplay();
            });
        }
    }

    _prepareModalForDisplay() {
        try {
            // Clear any stale form inputs from previous modal usage
            this._clearInputs();

            // Determine what locale type will be created based on the current store state.
            // This uses this controller instance's store connections (region, building, area, room)
            // which were initialized when this modal controller connected.
            const selectedLocaleTypeName = this._getNextLocaleType();

            // Update the modal title based on what type of locale we're about to create.
            // The title is updated by accessing this controller's titleTarget, which refers to the
            // h5 element within the modal (this controller's scope).
            if (this.hasTitleTarget) {
                this.titleTarget.textContent = `Create ${selectedLocaleTypeName}`;
            }

            // Add area-specific fields (floor, manager, floor plan upload) if creating an area
            if (selectedLocaleTypeName === LOCALE_TYPE_AREA) {
                this._addAreaFields();
            }
        } catch (error) {
            console.error('Error in _prepareModalForDisplay:', error);
            // If there's an error determining locale type, at least clear the inputs
            // so the modal is in a clean state
            this._clearInputs();
        }
    }

    handleAddButtonClick(event) {
        try {
            // Attempt to update the modal title immediately using the device form's controller
            // store connections. After the device form is submitted, these stores may not be
            // current, so this is a fallback mechanism only.
            if (this.hasAreaStoreValue?.id) {
                const selectedLocaleTypeName = this._getNextLocaleType();
                const titleElement = document.querySelector('h5[data-forms--locale-form-v2-target="title"]');
                if (titleElement) {
                    titleElement.textContent = `Create ${selectedLocaleTypeName}`;
                }
            }
        } catch (error) {
            // Silently fail - the window event dispatch below will handle it
        }

        // Dispatch a window event to notify the modal controller to prepare itself.
        // This is the primary mechanism because the device form's controller and the modal's
        // controller are separate Stimulus controller instances of the same class. The modal
        // controller has fresh store connections and should always be able to determine the
        // correct locale type to create. This pattern avoids cross-controller dependencies and
        // ensures we use the most up-to-date store subscriptions.
        window.dispatchEvent(new CustomEvent('walkthroughAddLocaleButtonClicked', {
            bubbles: true,
            composed: true
        }));
    }

    handleUpdateButtonClick(event) {
        // Clear out the inputs that may be dirty
        this._reenableSubmitButton();
        this._clearInputs();
        // this._removeAreaFields(); // This should be enabled and must be when this newer version is used for areas. I'm not sure why the controller can't find the placeholder target.

        // Get event details
        const localeId = event.detail.id;
        const localeType = event.detail.localeType;
        const localeName = event.detail.name;

        // Set the modal's values for update
        this.methodValue = 'patch';
        this.urlValue = `/api/locales/${_.toLower(localeType)}s/update`;

        // Set the modal's title
        this.titleTarget.textContent = `Update ${_.capitalize(localeType)}`;

        // Set the form's ID and name
        this.idInputTarget.value = localeId;
        this.nameInputTarget.value = localeName;

        // Add the area fields if updating an area
        if (localeType === LOCALE_TYPE_AREA) {
            this._addAreaFields();
            this._addExistingFloorPlanPreviewFields(localeId);
            this._populateAreaFields(localeId);
        }

        // Open the modal using Bootstrap 5 API
        const modal = bootstrap.Modal.getOrCreateInstance(this.modalTarget);
        modal.show();
    }

    handleSubmit(event) {
        // Prevent the default event operation from running
        event.preventDefault();

        // Determine what locale type is selected
        const selectedLocaleTypeName = this._getNextLocaleType();

        // Update the modal title to show what we're creating
        // This ensures the title is correct even if the "Add" button click handler didn't run
        if (this.hasTitleTarget) {
            this.titleTarget.textContent = `Create ${selectedLocaleTypeName}`;
        }

        // Set the modal's values. These will be used when saving the locale.
        this.methodValue = 'post'
        this.urlValue = `/api/locales/${_.toLower(selectedLocaleTypeName)}s/create`

        // Grab the route and method to call
        const url = _.toLower(this.urlValue);
        const method = _.toUpper(this.methodValue);

        // Grab the form data
        // const submitter = this.submitTarget;
        const formData = new FormData(event.target, event.submitter);

        // Determine parent ID from individual store contexts
        let parentId = this._getParentId();

        // Mix the soon-to-be-created locale's parent's ID into the form data.
        if (parentId !== null && !_.isEmpty(parentId)) {
            formData.set('parent_id', parentId);
        }

        // Issue the request
        $.ajax({
            url: url,
            method: method,
            dataType: 'json',
            format: 'json',
            data: formData,

            // This prevents jQuery from processing the FormData.
            // We need to do this to mix in additional values using FormData.append(...)
            processData: false,

            // Settings this to false lets the browser set the content type. This
            // is needed to mix in additional values using FormData.append(...)
            contentType: false,
        }).promise().then((result) => {
            const {name, id, localeType} = result;

            // Add the newly created item to the table
            // addItemToTable(localeType, id, name);

            // Create the event name. This determines how the new or updated locale is added/updated in the table.
            const eventName = `locale${_.toLower(method) === 'post' ? 'Created' : 'Updated'}`;

            // Create the details hash. We're doing this here to mix in additional fields if this is an area

            // Mix in those area attributes if this is an area
            if (localeType === LOCALE_TYPE_AREA) {
                this._updateAreaStore(result)
            }

            // Dispatch the event
            window.dispatchEvent(new CustomEvent(eventName, {
                detail: {
                    localeType: localeType,
                    id: id,
                    name: name
                },
                bubbles: true // This needs to be explicitly set to enable event bubbling
            }));

            // Also dispatch locale-specific events for different page contexts
            const specificEventName = `${localeType}${_.toLower(method) === 'post' ? 'Created' : 'Updated'}`;
            window.dispatchEvent(new CustomEvent(specificEventName, {
                detail: {
                    id: id,
                    name: name,
                    localeType: localeType
                },
                bubbles: true
            }));

            // Tell the user their request was successful
            sendSuccessNotification(`Successfully ${_.toLower(method) === 'post' ? 'created' : 'updated'} ${localeType} ${name}`);

            // Clear the inputs so the modal isn't dirty the next time someone clicks "add" or "edit"
            this._clearInputs();

            // This is hacky but for some reason the submit button is being disabled on submission.
            this._reenableSubmitButton();

            // Remove the area fields in case the next time the user opens this they aren't modifying an area
            this._removeAreaFields();

            // Close the modal
            const modalEl = document.getElementById('locale-form-modal');
            if (modalEl) {
                const bsModal = bootstrap.Modal.getInstance(modalEl);
                if (bsModal) {
                    bsModal.hide();
                } else {
                    const newModal = new bootstrap.Modal(modalEl);
                    newModal.hide();
                }
            }
            
            // Programmatically click the close button as fallback
            setTimeout(() => {
                const closeButton = document.querySelector('#locale-form-modal .btn-close');
                if (closeButton) {
                    closeButton.click();
                }
            }, 100);
        }).catch((error) => {
            // Use configured locale type
            const localeTypeName = this.localeTypeValue;
            
            // Safely get error message
            const errorMessage = error.responseJSON?.message || error.message || 'Unknown error';

            // Tell the user their request failed
            sendFailureNotification(`Failed to ${_.toLower(method) === 'post' ? 'create' : 'update'} a ${localeTypeName}.\n${errorMessage}`);

            // Clear the inputs so the modal isn't dirty the next time someone clicks "add" or "edit"
            this._clearInputs();

            // This is hacky but for some reason the submit button is being disabled on submission.
            this._reenableSubmitButton();

            // Remove the area fields in case the next time the user opens this they aren't modifying an area
            this._removeAreaFields();

            // Close the modal using Bootstrap 5 native API
            const modalEl = document.getElementById('locale-form-modal');
            if (modalEl) {
                const bsModal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
                bsModal.hide();
            }
        });
    }

    _getNextLocaleType() {
        // Grab the (possibly) selected IDs:
        const regionId = this.regionStoreValue?.id;
        const buildingId = this.buildingStoreValue?.id;
        const areaId = this.areaStoreValue?.id;
        const roomId = this.roomStoreValue?.id;

        // Determine what type of locale to create based on current store state
        if (_.isEmpty(regionId) && _.isEmpty(buildingId) && _.isEmpty(areaId) && _.isEmpty(roomId)) {
            // The user is viewing the regions table if nothing is selected.
            return LOCALE_TYPE_REGION;
        } else if (!_.isEmpty(regionId) && _.isEmpty(buildingId) && _.isEmpty(areaId) && _.isEmpty(roomId)) {
            // The user is viewing the buildings table if a region is selected.
            return LOCALE_TYPE_BUILDING;
        } else if (!_.isEmpty(regionId) && !_.isEmpty(buildingId) && _.isEmpty(areaId) && _.isEmpty(roomId)) {
            // The user is viewing the areas table if a region and building is selected.
            return LOCALE_TYPE_AREA;
        } else if (!_.isEmpty(regionId) && !_.isEmpty(buildingId) && !_.isEmpty(areaId) && _.isEmpty(roomId)) {
            // The user is viewing the rooms table if a region, building, and area is selected.
            return LOCALE_TYPE_ROOM;
        } else if (!_.isEmpty(regionId) && !_.isEmpty(buildingId) && !_.isEmpty(areaId) && !_.isEmpty(roomId)) {
            // A room is already selected (e.g. after creating a device in walkthrough mode).
            // The user wants to add another room in the same area.
            return LOCALE_TYPE_ROOM;
        } else {
            throw new Error('Unable to determine what facility is selected');
        }
    }

    _getParentId() {
        // Grab the (possibly) selected IDs:
        const regionId = this.regionStoreValue?.id;
        const buildingId = this.buildingStoreValue?.id;
        const areaId = this.areaStoreValue?.id;
        const roomId = this.roomStoreValue?.id;

        // Determine what type of locale to create based on current store state
        if (!_.isEmpty(regionId) && _.isEmpty(buildingId) && _.isEmpty(areaId) && _.isEmpty(roomId)) {
            return regionId;
        } else if (!_.isEmpty(regionId) && !_.isEmpty(buildingId) && _.isEmpty(areaId) && _.isEmpty(roomId)) {
            return buildingId;
        } else if (!_.isEmpty(regionId) && !_.isEmpty(buildingId) && !_.isEmpty(areaId)) {
            // Area is selected (with or without a room) — new room's parent is the area
            return areaId;
        } else {
            throw new Error('Unable to determine what facility is selected');
        }
    }

    _hasSelectedId(storeValue) {
        // Check if store has any selected ID (excluding metadata keys)
        const keys = Object.keys(storeValue).filter(key => !key.startsWith('selected'));
        return keys.length > 0;
    }

    _addAreaFields() {
        // Get the placeholder div. We'll add the new fields here.
        const placeholderDiv = this.placeholderTarget;

        /*
         * I'm doing this in raw HTML in Javascript instead of in a Rails partial or a data-template
         * since those methods require issuing a remote request to the controller to render the partial
         * or having these fields stubbed out but hidden in the form.
         */
        const container = document.createElement('div');
        container.classList.add(this.AREA_FIELDS_WRAPPER);
        container.innerHTML = `
            <div class="form-group mt-3">
                <label class="form-label" for="floor">Floor</label>
                <input placeholder="Enter floor" class="form-control mt-2" type="text" name="floor" id="floor" data-forms--locale-form-v2-target="floorInput">
            </div>
            <div class="form-group mt-3">
                <label class="form-label" for="manager">Manager</label>
                <input placeholder="Enter manager" class="form-control mt-2" type="text" name="manager" id="manager" data-forms--locale-form-v2-target="managerInput">
            </div>
            <div class="form-group mb-3">
                <label class="form-label" for="fileUpload">Floor plan</label>
                <input class="form-control area-form-floorplan form-control-file custom-file-input mt-2" id="fileUpload" type="file" name="floor_plan">
                <div class="form-text text-muted">Accepted formats: PDF, SVG, JPEG, PNG, WebP</div>
            </div>
            <div class="form-group mb-3">
                <div data-forms--locale-form-v2-target="floorPlanPreviewPlaceholder"></div>
            </div>
          `;

        placeholderDiv.appendChild(container);
    }

    _addExistingFloorPlanPreviewFields(id) {
        // Get the placeholder div. We'll add the new fields here.
        const placeholderDiv = this.floorPlanPreviewPlaceholderTarget;

        // Check if this area currently has a floor plan assigned
        const areaMetadata = this.areaStoreValue[id];
        const hasFloorPlan = !!(areaMetadata && areaMetadata.floor_plan !== null);

        // Check if this area currently has a floor plan assigned
        // const hasFloorPlan = this.areaStoreValue[id].floor_plan !== null;

        /*
         * I'm doing this in raw HTML in Javascript instead of in a Rails partial or a data-template
         * since those methods require issuing a remote request to the controller to render the partial
         * or having these fields stubbed out but hidden in the form.
         */
        const container = document.createElement('div');

        if (hasFloorPlan) {
            container.innerHTML = `
                <div class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
                      <!-- Photo Preview Section -->
                      <div class="card shadow-sm border-0" style="max-width: 300px;">
                        <div class="card-body text-center">
                          <!-- Preview Image -->
                          <img id="floor-plan-preview" 
                               src="" 
                               alt="Floor plan" 
                               class="img-fluid rounded mb-2"
                               style="max-height: 150px; object-fit: cover;"
                               data-forms--locale-form-v2-target="floorPlanPreview" 
                               />
                    
                          <!-- File Name -->
                          <p class="text-muted small">
                            <i class="bi bi-file-earmark-image"></i>
                            <span id="floor-plan-name" data-forms--locale-form-v2-target="floorPlanName"></span>
                          </p>
                        </div>
                      </div>
                    </div>
                </div>
            `;
        } else {
            container.innerHTML = `
                <div class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
                      <!-- Photo Preview Section -->
                      <div class="card shadow-sm border-0" style="max-width: 300px;">
                        <div class="card-body text-center">
                            <div class="d-flex justify-content-center align-items-center bg-secondary text-white rounded" 
                                 style="width: 300px; height: 200px;">This area does not have an  assigned floor plan</div>
                        </div>
                      </div>
                    </div>
                </div>
            `;
        }

        placeholderDiv.appendChild(container);
    }

    /**
     * This function updates the areaStore with the new area metadata when areas are updated
     * using the Update Area form or adds newly created areas when areas. It's necessary to
     * persist area metadata client side since we don't embed the non-name/ID information in
     * the Facilities Table and I don't want to issue another GET request to grab that to
     * populate the Update Area form.
     *
     * @param newOrUpdatedArea
     * @private
     */
    _updateAreaStore(newOrUpdatedArea) {
        // Const grab the new or updated area's ID
        const id = newOrUpdatedArea.id;

        // Grab the areas that have been fetched from the server
        const areas = this.areaStoreValue;

        // Grab the current area's metadata from the area store. This will only
        // be present when the user updated an existing area.
        let area = areas[id]

        // Check if this area exists. If it doesn't, return an empty hash.
        if (area === undefined) {
            area = {}
        }

        // Update the new or updated area metadata
        area.name = newOrUpdatedArea.name;
        area.floor = newOrUpdatedArea.floor;
        area.floor_plan = newOrUpdatedArea.floor_plan;
        area.floor_plan_url = newOrUpdatedArea.floor_plan_url;
        area.manager = newOrUpdatedArea.manager;

        // Put or replace the existing area at this id
        areas[id] = area;

        // Update the actual store value
        this.setAreaStoreValue(areas);
    }

    _populateAreaFields(id) {
        const areaMetadata = this.areaStoreValue[id];

        this.floorInputTarget.value = areaMetadata.floor;
        this.managerInputTarget.value = areaMetadata.manager;

        if (areaMetadata.floor_plan !== null && areaMetadata.floor_plan !== undefined && !_.isEmpty(areaMetadata.floor_plan)) {
            const floorPlanMetadata = JSON.parse(areaMetadata.floor_plan).metadata;
            const floorPlanName = floorPlanMetadata.filename;
            const floorPlanUrl = areaMetadata.floor_plan_url;

            // Add the existing floor plan's filename
            this.floorPlanNameTarget.textContent = floorPlanName;

            // Every stored floor plan is now a directly-displayable image
            // (WebP or SVG), so set the preview source directly.
            this.floorPlanPreviewTarget.src = floorPlanUrl;
        }
    }

    _clearInputs() {
        // The submit button is actually an input element. We have to be careful with how
        // we clear inputs or we'll bash the "Submit" value. Using the form-control class
        // is good enough.
        document.querySelectorAll('input.form-control').forEach(input => {
            input.value = '';
        });
    }

    /**
     * This is hacky but for some reason the value of the submit button is being cleared.
     * Let's reset it so this form is usable again
     * @private
     */
    _reenableSubmitButton() {
        this.submitTarget.value = 'Submit';
        this.submitTarget.disabled = false;
    }

    _removeAreaFields() {
        if (this.hasPlaceholderTarget) {
            this.placeholderTarget.replaceChildren();
        }
    }

    _removeFloorPlanPreviewFields() {
        this.floorPlanPreviewTarget.replaceChildren();
    }

    // Helper method to get selected ID from store based on context
    _getSelectedStoreId(storeType) {
        switch (storeType) {
            case 'region':
                if (this.hasRegionStoreValue) {
                    return this.regionStoreValue.selectedRegionId ||
                        Object.keys(this.regionStoreValue).find(key => !key.startsWith('selected'));
                }
                return null;
            case 'building':
                if (this.hasBuildingStoreValue) {
                    return this.buildingStoreValue.selectedBuildingId ||
                        Object.keys(this.buildingStoreValue).find(key => !key.startsWith('selected'));
                }
                return null;
            case 'area':
                if (this.hasAreaStoreValue) {
                    return this.areaStoreValue.selectedAreaId ||
                        Object.keys(this.areaStoreValue).find(key => !key.startsWith('selected'));
                }
                return null;
            default:
                return null;
        }
    }
}